import { Task } from "@lit/task";
import { getRange, GetSavingsReportsParams, monthNames } from "@pentacode/core";
import type { Euros } from "@pentacode/openapi";
import type { ChartDefinition } from "@pentacode/ui/charts";
import { formatCurrency, savingsChart } from "@pentacode/ui/charts";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { app } from "../../../../init";
import { shared } from "../../../../styles";
import { contactSupport } from "../../../../vendor/helpscout";
import { tileSavings } from "../../../reports/savings-days";
import "./base-tile";

@customElement("ptc-dashboard-tile-savings-report")
export class DashboardTileSavingsDaily extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.savingsReport.size;

    @state()
    private _year: number = new Date().getFullYear();

    @state()
    private _month: number = new Date().getMonth();

    @state()
    private _totalSavings: Euros = 0 as Euros;

    @state()
    private _serviceFees: Euros = 0 as Euros;

    @state()
    private _chartDefinition?: ChartDefinition;

    private _savingsTask = new Task(this, {
        task: async ([api]) => {
            const currMonth = getRange(new Date(this._year, this._month, 1), "month");

            const dailyReports = await api.getSavingsReports(
                new GetSavingsReportsParams({ ...currMonth, resolution: "day" })
            );

            this._totalSavings = dailyReports.reduce((total, report) => total + report.savings, 0) as Euros;
            this._serviceFees = dailyReports.reduce((total, report) => total + report.fees, 0) as Euros;
            const { days, fees } = tileSavings(dailyReports, this._year, this._month);
            this._chartDefinition = savingsChart(days, fees);

            return dailyReports;
        },
        args: () => [app.api, this._year, this._month] as const,
        autoRun: true,
    });

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }

            .tile-link {
                display: grid;
                grid-template-rows: minmax(0, 1fr) auto;
                height: 100%;
                color: inherit;
                text-decoration: none;
            }

            .chart-container {
                display: grid;
                min-height: 0;
                width: 100%;
                padding: 0;
                margin: 0;
            }

            .chart-savings-daily {
                min-height: 8rem;
                width: 100%;
            }

            .savings-summary {
                display: flex;
                flex-direction: row;
                align-items: stretch;
                justify-content: space-around;
                padding: 0.75rem 0.5rem;
                font-size: var(--font-size-small);
                border-top: 1px solid var(--shade-2);
                gap: 0.5rem;
            }

            .savings-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 0.25rem;
                flex: 1;
                min-width: 0;
                padding: 0 0.5rem;
            }

            .savings-item:not(:last-child) {
                border-right: 1px solid var(--shade-2);
            }

            .savings-label {
                color: var(--text-soft);
                font-size: 0.9rem;
                letter-spacing: 0.5px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                opacity: 0.8;
                text-align: center;
            }

            .savings-amount {
                font-weight: 700;
                font-size: 0.95rem;
                line-height: 1.2;
            }

            .savings-amount.positive {
                color: var(--green);
            }

            .savings-amount.negative {
                color: var(--red);
            }

            .savings-amount.neutral {
                color: var(--blue);
            }

            .empty-message {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100%;
                color: var(--text-soft);
                text-align: center;
                padding: 1rem;
            }

            .savings-preview {
                display: grid;
                min-height: 0;
            }

            .savings-preview > * {
                grid-area: 1 / 1;
            }

            .savings-preview ptc-ui-chart {
                opacity: 0.25;
                pointer-events: none;
            }

            .savings-teaser-section {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 1rem;
                padding: 1.5rem;
            }

            .savings-teaser-text {
                font-size: 0.9rem;
                text-align: center;
                color: var(--black);
                background: var(--bg-light);
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--green)"
                label="Ersparnis"
                .subLabel=${`${monthNames[this._month]} ${this._year}`}
                icon="icon.object.bank-deposit"
                .size=${this.size}
                .allowResize=${false}
                tileType="savingsReport"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                <a class="tile-link" href="/reports/savings?year=${this._year}&amp;month=${this._month}">
                    ${this._savingsTask.render({
                        initial: () => html``,
                        pending: () =>
                            html`<ptc-ui-spinner
                                class="double-padded place-self-center"
                                active
                                style="font-size: 1rem"
                            ></ptc-ui-spinner>`,
                        error: () =>
                            html`<div class="empty-message">Ersparnisdaten konnten nicht geladen werden!</div>`,
                        complete: (dailyReports) => {
                            if (dailyReports.length === 0 || this._totalSavings === 0) {
                                return html`<div class="savings-preview">
                                    <ptc-ui-chart
                                        label="Beispielhafte Ersparnisgrafik: kumulierte Einsparung im Monat im Vergleich zu den Gebühren"
                                        fill
                                        .definition=${this._chartDefinition}
                                    ></ptc-ui-chart>
                                    <div class="savings-teaser-section">
                                        <p class="savings-teaser-text">
                                            Der Ersparnisbericht erfordert die Nutzung der elektronischen Zeiterfassung
                                            und Zeiterfassungsdaten.
                                        </p>
                                        <button
                                            class="primary"
                                            @click=${(e: Event) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                contactSupport("Ersparniskurve Freischalten");
                                            }}
                                        >
                                            <ptc-ui-icon name="icon.action.contact-support"></ptc-ui-icon>
                                            Support Kontaktieren
                                        </button>
                                    </div>
                                </div>`;
                            }

                            return html`
                                <div class="chart-container">
                                    <ptc-ui-chart
                                        class="chart-savings-daily"
                                        label="Ersparnis: kumulierte Einsparung im Monat im Vergleich zu den Gebühren"
                                        fill
                                        .definition=${this._chartDefinition}
                                    ></ptc-ui-chart>
                                </div>
                                <div class="savings-summary">
                                    <div class="savings-item">
                                        <div class="savings-label">Ersparnis</div>
                                        <div class="savings-amount neutral">${formatCurrency(this._totalSavings)}</div>
                                    </div>
                                    <div class="savings-item">
                                        <div class="savings-label">Gebühren</div>
                                        <div class="savings-amount negative">${formatCurrency(this._serviceFees)}</div>
                                    </div>
                                    <div class="savings-item">
                                        <div class="savings-label">Netto-Ersparnis</div>
                                        <div
                                            class="savings-amount ${this._totalSavings - this._serviceFees >= 0
                                                ? "positive"
                                                : "negative"}"
                                        >
                                            ${formatCurrency(this._totalSavings - this._serviceFees)}
                                        </div>
                                    </div>
                                </div>
                            `;
                        },
                    })}
                </a>
            </ptc-dashboard-tile>
        `;
    }
}
